<template>
  <div class="container py-4">
    <div class="page-card">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">Products</h4>
        <div class="d-flex gap-2">
          <icon-button
            :func="() => (showImport = true)"
            :icon="'upload'"
            :title="'Import products'"
            :classes="'akkurate-gray-btn btn-size2'"
            :btnName="'Import products'"
          />
          <icon-button
            :func="() => (showCreate = true)"
            :icon="'plus'"
            :title="'Add product'"
            :classes="'akkurate-green-btn btn-size2'"
            :btnName="'Add product'"
          />
          <icon-button
            :func="() => openVariantModal()"
            :icon="'plus'"
            :title="'Add variant'"
            :classes="'akkurate-link-btn btn-size2'"
            :btnName="'Add variant'"
          />
        </div>
      </div>

      <b-table
        :items="products"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
      >
        <template #cell(name)="data">
          <span>{{ data.value }}</span>
          <b-badge
            v-if="data.item.variantCount > 0"
            variant="light"
            class="ms-1 badge akkurate-link-btn"
          >
            {{ data.item.variantCount }} variants
          </b-badge>
          <b-badge
            v-if="data.item.parentId"
            class="ms-1 badge akkurate-link-btn"
            >Variant</b-badge
          >
        </template>
        <template #cell(variantAttributes)="data">
          <span v-if="data.value">{{ formatAttributes(data.value) }}</span>
          <span v-else>—</span>
        </template>
        <template #cell(unitPrice)="data">
          {{ formatMoney(data.value || 0) }}
        </template>
        <template #cell(stock)="data">
          {{ data.value?.available ?? "—" }}
        </template>
        <template #cell(isActive)="data">
          <b-badge
            :variant="data.value ? 'success' : 'secondary'"
            :class="statusBadge(data.value)"
          >
            {{ data.value ? "Active" : "Inactive" }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <!-- <b-button
            
            size="sm"
            variant="link"
            @click="toggleVariants(data.item)"
          >
            {{ expandedParentId === data.item.id ? "Hide" : "Variants" }}
          </b-button> -->
          <icon-button
            v-if="data.item.variantCount > 0"
            :func="() => toggleVariants(data.item)"
            :icon="'eye'"
            :title="'View variants'"
            :classes="'akkurate-blue btn-sm'"
            :btnName="''"
          />
          <icon-button
            :func="() => toggleView(data.item)"
            :icon="'eye'"
            :title="'View Product'"
            :classes="'akkurate-orange btn-sm'"
            :btnName="''"
          />

          <icon-button
            :func="() => toggleUpdate(data.item)"
            :icon="'edit'"
            :title="'Edit Product'"
            :classes="'akkurate-blue btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>

      <b-table
        v-if="variantRows.length"
        :items="variantRows"
        :fields="variantFields"
        small
        responsive
        class="mt-2 ms-3 border-start ps-3"
      >
        <template #cell(variantAttributes)="data">
          {{ formatAttributes(data.value) }}
        </template>
        <template #cell(unitPrice)="data">{{
          formatMoney(data.value)
        }}</template>
        <template #cell(stock)="data">{{
          data.value?.available ?? "—"
        }}</template>
      </b-table>
    </div>

    <b-modal v-model="showCreate" title="New product" @ok="createProduct">
      <b-form-group label="Name">
        <b-form-input class="form-control" v-model="form.name" required />
      </b-form-group>
      <b-form-group label="SKU">
        <b-form-input class="form-control" v-model="form.sku" required />
      </b-form-group>
      <b-form-group label="Barcode">
        <b-form-input class="form-control" v-model="form.barcode" />
      </b-form-group>
      <b-form-group label="Category">
        <b-form-input class="form-control" v-model="form.category" />
      </b-form-group>
      <b-form-group label="Unit price">
        <b-form-input
          class="form-control"
          v-model="form.unitPrice"
          type="number"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Cost price">
        <b-form-input
          class="form-control"
          v-model="form.costPrice"
          type="number"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Tax / VAT rate (%)">
        <b-form-input
          class="form-control"
          v-model="form.taxRate"
          type="number"
          min="0"
          max="100"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Opening stock qty">
        <b-form-input
          class="form-control"
          v-model="form.initialQuantity"
          type="number"
          min="0"
          step="1"
        />
        <b-form-text
          >Stock for the current store: {{ selectedStoreName }}</b-form-text
        >
      </b-form-group>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Save'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save'"
        />
      </template>
    </b-modal>

    <b-modal v-model="showVariant" title="New variant" @ok="createVariant">
      <b-form-group label="Parent product">
        <b-form-select
          v-model="variantForm.parentId"
          :options="parentOptions"
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          required
        />
      </b-form-group>
      <b-form-group label="Variant name">
        <b-form-input
          class="form-control"
          v-model="variantForm.name"
          placeholder="e.g. Blue / Large"
        />
      </b-form-group>
      <b-form-group label="SKU">
        <b-form-input class="form-control" v-model="variantForm.sku" required />
      </b-form-group>
      <b-form-group label="Barcode">
        <b-form-input class="form-control" v-model="variantForm.barcode" />
      </b-form-group>
      <b-form-group label="Size">
        <b-form-input class="form-control" v-model="variantForm.size" />
      </b-form-group>
      <b-form-group label="Color">
        <b-form-input class="form-control" v-model="variantForm.color" />
      </b-form-group>
      <b-form-group label="Unit price">
        <b-form-input
          class="form-control"
          v-model="variantForm.unitPrice"
          type="number"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Cost price">
        <b-form-input
          class="form-control"
          v-model="variantForm.costPrice"
          type="number"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Opening stock qty">
        <b-form-input
          class="form-control"
          v-model="variantForm.initialQuantity"
          type="number"
          min="0"
        />
      </b-form-group>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Save'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save'"
        />
      </template>
    </b-modal>

    <bulk-import-modal
      v-model="showImport"
      title="Import products"
      template-endpoint="import/products/template"
      import-endpoint="import/products"
      :store-id="selectedStoreId"
      :showOverwriteQty="true"
      @imported="load"
    />

    <product-edit
      ref="productEditModal"
      :productId="selectedProductId"
      :selectedStoreName="selectedStoreName"
      :storeId="selectedStoreId"
      @updated="handleProductUpdated"
    />

    <product-view
      ref="productViewModal"
      :productId="selectedProductId"
      :selectedStoreName="selectedStoreName"
      :storeId="selectedStoreId"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import BulkImportModal from "@/views/main/pages/shared/BulkImportModal.vue";
import IconButton from "@/components/IconButton.vue";
import ProductEdit from "@/components/ProductEdit.vue";
import ProductView from "@/components/ProductView.vue";

export default {
  name: "Products",
  components: { BulkImportModal, IconButton, ProductEdit, ProductView },
  data() {
    return {
      products: [],
      loading: false,
      showCreate: false,
      showVariant: false,
      showImport: false,
      showUpdate: false,
      expandedParentId: null,
      selectedProductId: null,
      variantRows: [],
      form: {
        name: "",
        sku: "",
        barcode: "",
        unitPrice: 0,
        costPrice: 0,
        taxRate: 0,
        category: "General",
        initialQuantity: 0,
      },
      variantForm: {
        parentId: "",
        name: "",
        sku: "",
        barcode: "",
        size: "",
        color: "",
        unitPrice: 0,
        costPrice: 0,
        initialQuantity: 0,
      },
      fields: [
        { key: "sku", label: "SKU" },
        { key: "name", label: "Name" },
        { key: "variantAttributes", label: "Attributes" },
        { key: "unitPrice", label: "Price" },
        { key: "taxRate", label: "Tax %" },
        { key: "stock", label: "Available" },
        { key: "isActive", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
      variantFields: [
        { key: "sku", label: "SKU" },
        { key: "name", label: "Name" },
        { key: "variantAttributes", label: "Attributes" },
        { key: "unitPrice", label: "Price" },
        { key: "stock", label: "Available" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId", "selectedStore"]),
    selectedStoreName() {
      return this.selectedStore?.storeName || "—";
    },
    parentOptions() {
      return [
        { value: "", text: "Select parent" },
        ...this.products
          .filter((p) => !p.parentId)
          .map((p) => ({ value: p.id, text: p.name })),
      ];
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async handleProductUpdated() {
      await this.load();
    },
    toggleView(product) {
      this.selectedProductId = product.id;
      this.$refs.productViewModal.$bvModal.show("productView");
    },
    formatAttributes(attrs) {
      if (!attrs || typeof attrs !== "object") return "—";
      return Object.entries(attrs)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    },
    async load() {
      if (!this.selectedStoreId) return;
      this.loading = true;
      try {
        this.products = await ApiService.get("products", {
          storeId: this.selectedStoreId,
        });
        if (this.expandedParentId) {
          await this.loadVariants(this.expandedParentId);
        }
      } finally {
        this.loading = false;
      }
    },
    async toggleVariants(product) {
      if (this.expandedParentId === product.id) {
        this.expandedParentId = null;
        this.variantRows = [];
        return;
      }
      this.expandedParentId = product.id;
      await this.loadVariants(product.id);
    },
    toggleUpdate(product) {
      this.selectedProductId = product.id;
      this.$refs.productEditModal.$bvModal.show("productEdit");
    },
    async loadVariants(parentId) {
      const variants = await ApiService.get("products", {
        storeId: this.selectedStoreId,
        parentId,
      });
      this.variantRows = variants;
    },
    openVariantModal() {
      this.variantForm = {
        parentId: "",
        name: "",
        sku: "",
        barcode: "",
        size: "",
        color: "",
        unitPrice: 0,
        costPrice: 0,
        initialQuantity: 0,
      };
      this.showVariant = true;
    },
    async createProduct(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        await ApiService.post("products", {
          ...this.form,
          storeId: this.selectedStoreId,
          unitPrice: Number(this.form.unitPrice),
          costPrice: Number(this.form.costPrice),
          taxRate: Number(this.form.taxRate) || 0,
          initialQuantity: Number(this.form.initialQuantity) || 0,
        });
        this.showCreate = false;
        this.resetForm();
        await this.load();
        toast.fire({ title: "Product created", icon: "success" });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Create failed",
          icon: "danger",
        });
      }
    },
    async createVariant(bvModalEvt) {
      bvModalEvt.preventDefault();
      const parent = this.products.find(
        (p) => p.id === this.variantForm.parentId,
      );
      const attrs = {};
      if (this.variantForm.size) attrs.size = this.variantForm.size;
      if (this.variantForm.color) attrs.color = this.variantForm.color;

      const name =
        this.variantForm.name ||
        [parent?.name, this.variantForm.size, this.variantForm.color]
          .filter(Boolean)
          .join(" — ");

      try {
        await ApiService.post("products", {
          name,
          sku: this.variantForm.sku,
          barcode: this.variantForm.barcode || null,
          parentId: this.variantForm.parentId,
          variantAttributes: Object.keys(attrs).length ? attrs : null,
          category: parent?.category || "General",
          unitPrice:
            Number(this.variantForm.unitPrice) || parent?.unitPrice || 0,
          costPrice:
            Number(this.variantForm.costPrice) || parent?.costPrice || 0,
          taxRate: parent?.taxRate || 0,
          storeId: this.selectedStoreId,
          initialQuantity: Number(this.variantForm.initialQuantity) || 0,
        });
        this.showVariant = false;
        await this.load();
        toast.fire({ title: "Variant created", icon: "success" });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Create failed",
          icon: "danger",
        });
      }
    },
    resetForm() {
      this.form = {
        name: "",
        sku: "",
        barcode: "",
        unitPrice: 0,
        costPrice: 0,
        taxRate: 0,
        category: "General",
        initialQuantity: 0,
      };
    },
  },
};
</script>
